import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AppWindow,
  ChevronLeft,
  Info,
  KeyRound,
  MousePointerClick,
  Plus,
  Rocket,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { notiApi, type NotiClient } from "../lib/api-noti";
import { CardTabs } from "../components/CardTabs";
import { Toggle } from "../components/Toggle";
import { SecretRevealModal } from "./ClientsPage";

const TABS = ["Overview", "Credentials", "Action", "Quickstart"] as const;

export default function ClientDetailPage() {
  const { clientId } = useParams({ strict: false }) as { clientId: string };
  const [tab, setTab] = useState<(typeof TABS)[number]>("Overview");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [reveal, setReveal] = useState<{
    client: NotiClient;
    secret: string;
  } | null>(null);

  const client = useQuery({
    queryKey: ["noti-client", clientId],
    queryFn: () => notiApi.getClient(clientId),
  });

  const rotate = useMutation({
    mutationFn: () => notiApi.rotateClientSecret(clientId),
    onSuccess: (r) => {
      setReveal(r);
      qc.invalidateQueries({ queryKey: ["noti-client", clientId] });
    },
  });
  const remove = useMutation({
    mutationFn: () => notiApi.deleteClient(clientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["noti-clients"] });
      navigate({ to: "/clients" });
    },
  });

  const c = client.data;
  if (client.isLoading) return <div className="panel">กำลังโหลด…</div>;
  if (client.isError || !c)
    return (
      <div className="panel error">
        {(client.error as Error)?.message ?? "ไม่พบ client"}
      </div>
    );

  return (
    <>
      <div className="page-head">
        <h2>
          <Link to="/clients" className="back-link">
            <ChevronLeft size={18} />
          </Link>
          <AppWindow size={20} /> {c.name}{" "}
          {c.isAdmin && <ShieldCheck size={16} className="admin-mark" />}
          <code className="head-code">{c.id}</code>
        </h2>
      </div>

      <CardTabs<(typeof TABS)[number]>
        tabs={[
          { key: "Overview", label: "Overview", icon: Info },
          {
            key: "Credentials",
            label: "Credentials",
            icon: KeyRound,
            badge: c.hasSecret ? undefined : "!",
            badgeTone: "warn",
          },
          { key: "Action", label: "Action", icon: MousePointerClick },
          { key: "Quickstart", label: "Quickstart", icon: Rocket },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "Overview" && <OverviewTab c={c} />}
      {tab === "Credentials" && (
        <div className="panel">
          <h3>
            <KeyRound size={16} /> Client Secret
          </h3>
          <dl className="kv">
            <dt>สถานะ</dt>
            <dd>
              {c.hasSecret ? (
                <>
                  ออกแล้ว — <code>ncs_…{c.secretHint}</code>
                </>
              ) : (
                <span className="pill warn">ยังไม่ออก secret</span>
              )}
            </dd>
            <dt>Rotate ล่าสุด</dt>
            <dd>
              {c.secretRotatedAt
                ? new Date(c.secretRotatedAt).toLocaleString("th-TH")
                : "—"}
            </dd>
          </dl>
          <p className="muted small">
            การ rotate ทำให้ secret เดิมใช้ไม่ได้ทันที (cache ฝั่ง engine
            ถูกล้าง) — ประสานทีมเจ้าของระบบก่อนทุกครั้ง
          </p>
          {rotate.isError && (
            <div className="error">{(rotate.error as Error).message}</div>
          )}
          <button
            className="btn-primary"
            disabled={rotate.isPending}
            onClick={() => {
              if (
                confirm(
                  `ยืนยัน rotate secret ของ ${c.id}? secret เดิมจะใช้ไม่ได้ทันที`,
                )
              )
                rotate.mutate();
            }}
          >
            {rotate.isPending
              ? "กำลังออก secret…"
              : c.hasSecret
                ? "Rotate Secret"
                : "ออก Secret ครั้งแรก"}
          </button>
        </div>
      )}
      {tab === "Action" && <ActionTab clientId={c.id} />}
      {tab === "Quickstart" && <QuickstartTab c={c} />}

      {!c.isAdmin && tab === "Overview" && (
        <div className="panel danger-zone">
          <h3>ลบ Client</h3>
          <p className="muted small" style={{ margin: "0 0 0.6rem" }}>
            ลบได้เมื่อไม่มี workflow destination อ้างถึง — ประวัติธุรกรรม
            (events.source) คงอยู่
          </p>
          {remove.isError && (
            <div className="error">{(remove.error as Error).message}</div>
          )}
          <button
            className="btn-danger"
            disabled={remove.isPending}
            onClick={() => {
              if (confirm(`ยืนยันลบ client ${c.id}?`)) remove.mutate();
            }}
          >
            <Trash2 size={15} /> ลบ Client นี้
          </button>
        </div>
      )}

      {reveal && (
        <SecretRevealModal {...reveal} onClose={() => setReveal(null)} />
      )}
    </>
  );
}

function OverviewTab({ c }: { c: NotiClient }) {
  const qc = useQueryClient();
  const [name, setName] = useState(c.name);
  const [description, setDescription] = useState(c.description);
  const save = useMutation({
    mutationFn: () => notiApi.patchClient(c.id, { name, description }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["noti-client", c.id] }),
  });
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => notiApi.patchClient(c.id, { enabled }),
    onSettled: () => qc.invalidateQueries({ queryKey: ["noti-client", c.id] }),
  });

  return (
    <div className="panel">
      <div className="form-grid">
        <label>
          ชื่อระบบ
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label>
          คำอธิบาย
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </label>
        <label>
          ประเภท
          <input value={c.type} disabled />
        </label>
        <label>
          สถานะการใช้งาน
          <div className="toggle-row">
            <Toggle
              checked={c.enabled}
              disabled={c.isAdmin || toggle.isPending}
              onChange={(v) => toggle.mutate(v)}
            />
            <span className="muted small">
              {c.enabled
                ? "เปิดใช้งาน — authenticate ได้"
                : "ปิด — ทุกคำขอถูกปฏิเสธทันที"}
            </span>
          </div>
        </label>
      </div>
      {save.isError && (
        <div className="error">{(save.error as Error).message}</div>
      )}
      <button
        className="btn-primary"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        <Save size={15} /> บันทึก
      </button>
    </div>
  );
}

// Keycloak-style attributes: key→value pairs the bell/library reads at runtime.
// Surfaced in the UI as "Action" — what happens when the user clicks a notification.
function ActionTab({ clientId }: { clientId: string }) {
  const qc = useQueryClient();
  const attrs = useQuery({
    queryKey: ["noti-client-attrs", clientId],
    queryFn: () => notiApi.clientAttributes(clientId),
  });
  const [rows, setRows] = useState<{ key: string; value: string }[]>([]);

  useEffect(() => {
    if (attrs.data)
      setRows(
        Object.entries(attrs.data.attributes).map(([key, value]) => ({
          key,
          value,
        })),
      );
  }, [attrs.data]);

  const save = useMutation({
    mutationFn: () => {
      const next: Record<string, string> = {};
      for (const r of rows)
        if (r.key.trim() !== "") next[r.key.trim()] = r.value;
      // Keys removed in the editor are sent as empty values → deleted (Keycloak semantics).
      for (const existing of Object.keys(attrs.data?.attributes ?? {})) {
        if (!(existing in next)) next[existing] = "";
      }
      return notiApi.putClientAttributes(clientId, next);
    },
    onSettled: () =>
      qc.invalidateQueries({ queryKey: ["noti-client-attrs", clientId] }),
  });

  return (
    <div className="panel">
      <h3>Action</h3>
      <p className="muted small" style={{ margin: "0 0 0.7rem" }}>
        กำหนดการกระทำเมื่อผู้ใช้คลิกรายการแจ้งเตือนในแอปนี้ — คู่ key→value ที่
        library ฝั่ง client (เช่น @baacloan/noti-react) อ่านตอน runtime เช่น{" "}
        <code>click.action</code>, <code>click.urlTemplate</code>{" "}
        ปรับแล้วมีผลทันทีโดยไม่ต้อง deploy แอป
      </p>
      <KvEditor
        rows={rows}
        onChange={setRows}
        keyPlaceholder="click.urlTemplate"
        valuePlaceholder="https://…/inbox?subscriber={{subscriber}}"
      />
      {save.isError && (
        <div className="error">{(save.error as Error).message}</div>
      )}
      <div className="modal-actions" style={{ marginTop: "0.8rem" }}>
        <button
          className="btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          <Save size={15} /> บันทึก Action
        </button>
      </div>
    </div>
  );
}

export function KvEditor({
  rows,
  onChange,
  keyPlaceholder,
  valuePlaceholder,
}: {
  rows: { key: string; value: string }[];
  onChange: (rows: { key: string; value: string }[]) => void;
  keyPlaceholder?: string;
  valuePlaceholder?: string;
}) {
  const set = (i: number, field: "key" | "value", v: string) => {
    const next = rows.slice();
    next[i] = { ...next[i], [field]: v };
    onChange(next);
  };
  return (
    <div className="kv-editor">
      {rows.map((r, i) => (
        <div className="kv-row" key={i}>
          <input
            className="kv-key"
            value={r.key}
            spellCheck={false}
            placeholder={keyPlaceholder}
            onChange={(e) => set(i, "key", e.target.value)}
          />
          <input
            className="kv-value"
            value={r.value}
            spellCheck={false}
            placeholder={valuePlaceholder}
            onChange={(e) => set(i, "value", e.target.value)}
          />
          <button
            className="icon-ghost"
            aria-label="ลบแถว"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        className="btn-ghost"
        onClick={() => onChange([...rows, { key: "", value: "" }])}
      >
        <Plus size={14} /> เพิ่มพารามิเตอร์
      </button>
    </div>
  );
}

function QuickstartTab({ c }: { c: NotiClient }) {
  const curl = `curl -X POST https://notification.blockfint.com/v1/events/trigger \\
  -H "X-Client-Id: ${c.id}" \\
  -H "X-Client-Secret: <secret ของท่าน>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "<workflow key>",
    "to": [{"subscriberId": "CIF-xxxxxxx"}],
    "payload": { "..." : "ตาม payload schema ของ workflow" },
    "transactionId": "<เลขอ้างอิงฝั่งระบบท่าน — กันส่งซ้ำ>"
  }'`;
  return (
    <div className="panel">
      <h3>Quickstart — การเรียกใช้จากระบบ {c.name}</h3>
      <ol className="steps-list">
        <li>
          รับ <b>Client Secret</b> จากแท็บ Credentials (แสดงครั้งเดียว)
        </li>
        <li>
          แนบ header <code>X-Client-Id</code> + <code>X-Client-Secret</code>{" "}
          ทุกคำขอ
          <span className="muted">
            {" "}
            (ระหว่างเปลี่ยนผ่านยังใช้ <code>Authorization: ApiKey</code>{" "}
            แบบเดิมได้)
          </span>
        </li>
        <li>
          ยิงเหตุการณ์เข้า trigger API — workflow ที่อนุญาต client
          นี้เท่านั้นจึงรับ
        </li>
      </ol>
      <pre className="code-block">{curl}</pre>
      <p
        className="small"
        style={{
          margin: "0 0 0.6rem",
          padding: "0.45rem 0.7rem",
          borderLeft: "3px solid var(--warn)",
          background: "var(--surface-2)",
          borderRadius: "0 6px 6px 0",
        }}
      >
        ⚠️ ค่า <code>ncs_…</code> คือ <b>client secret</b> — ใส่ที่{" "}
        <code>X-Client-Secret</code> (คู่กับ <code>X-Client-Id</code>) เท่านั้น{" "}
        <b>ห้าม</b>เอาไปใส่ใน <code>Authorization: ApiKey</code> (คนละระบบ จะได้{" "}
        <code>unknown API key</code>). ส่วน <code>Authorization: ApiKey</code>{" "}
        ใช้กับ ApiKey รายระบบแบบเดิม (สตริง <code>source:key</code>) ไม่ใช่ค่า{" "}
        <code>ncs_…</code>
      </p>
      <p className="muted small">
        สเปคฉบับสาธารณะ: เมนู <b>API Docs</b> · ระยะถัดไป (AUTH_MODE=oidc)
        ต้องแนบ
        <code>
          {" "}
          Authorization: Bearer &lt;JWT ผู้ใช้จาก UMS/Keycloak&gt;
        </code>{" "}
        เพิ่ม
      </p>
    </div>
  );
}
